import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import Register from './Register';
import VerifyEmail from './VerifyEmail';
import ResetPassword from './ResetPassword';
import Dashboard from './Dashboard';
import Upgrade from './Upgrade';
import Manage from './Manage';
import Generators from './Generators';
import BattlemapGenerators from './BattlemapGenerators';
import SearchResults from './SearchResults';
import { PaymentSuccess, PaymentCancel } from './PaymentPages';
import AccountSuspended from './AccountSuspended';
import AccountLocked from './AccountLocked';
import GeneratingResult from './GeneratingResult';
import Workspace from './Workspace';
import AnimatePage from './AnimatePage';
import AnimationCreditsPage from './AnimationCreditsPage';
import AiRebuildPage from './AiRebuildCyberTechPage';
import BattlemapsEngines from './BattlemapsEngines';
import BattlemapsPricing from './BattlemapsPricing';
import BattlemapsPacks from './BattlemapsPacks';
import DnDEngines from './DnDEngines';
import ProfessionalEngines from './ProfessionalEngines';
import EngineWrapper from './EngineWrapper';
import StaffDashboard from './StaffDashboard';
import SecurityAdminOps from './SecurityAdminOps';
import ServiceCommandCentreMasterControl from './ServiceCommandCentreMasterControl';
import RewardsPage from './RewardsPage';
import QuickEditPage from './QuickEditPage';
import GrowthPricing from './GrowthPricing';
import GeneratorGrowthPricing from './GeneratorGrowthPricing';
import BattleTracker from './BattleTracker';
import CombatProPlayerMessagesPage from './CombatProPlayerMessagesPage';
import CombatProPricingPage from './CombatProPricingPage';
import CombatProMembershipPage from './CombatProMembershipPage';
import CombatProPurchaseHistory from './CombatProPurchaseHistory';
import StandaloneGeneratorsPage from './StandaloneGeneratorsPage';
import RpgPage from './RpgPage';
import GamingPage from './GamingPage';
import VirtualShopPage from './VirtualShopPage';
import RpgPricingPage from './RpgPricingPage';
import VeridianPurchasePage from './VeridianPurchasePage';
import VeridianCharactersPage from './VeridianCharactersPage';
import VeridianMoreInfoPage from './VeridianMoreInfoPage';
import CyberTechPurchasePage from './CyberTechPurchasePage';
import CyberTechMoreInfoPage from './CyberTechDetailPage';
import SecurityPage from './SecurityPage';
import SecurityDashboard from './SecurityDashboard';
import SessionGuardSetup from './SessionGuardSetup';
import SessionGuardEvidence from './SessionGuardEvidence';
import ProductDetailPage from './ProductDetailExperience';
import UtilitiesPage from './UtilitiesPage';
import EmailAIPage from './EmailAIPage';
import FreeUtilitiesPage from './FreeUtilitiesPage';
import PaidUtilitiesPage from './PaidUtilitiesPage';
import BackupUtilityPage from './BackupUtilityPage';
import PdfToolkitPage from './PdfToolkitPage';
import RegistryUtilitiesPage from './RegistryUtilitiesPage';
import RegistryCategoryPage from './RegistryCategoryPage';
import TechHubPage from './TechHubPage';
import TechHubToolPage from './TechHubToolPage';
import DesktopCommandCenter from './DesktopCommandCenter';
import TechnicianCloudHistory from './TechnicianCloudHistory';
import TechnicianApplication from './TechnicianApplication';
import SupportModal from './components/SupportModal';
import GoToTop from './components/GoToTop';
import { StaffAuthProvider } from './StaffAuthContext';
import StaffLogin from './StaffLogin';
import StaffMFASetup from './StaffMFASetup';
import StaffMFAVerify from './StaffMFAVerify';
import StaffMFAElevate from './StaffMFAElevate';
import StaffInviteAccept from './StaffInviteAccept';
import StaffResetPassword from './StaffResetPassword';
import StaffMFARecovery from './StaffMFARecovery';
import StaffAdminRouteGuard, { StaffAdminNamespaceFallback } from './StaffAdminRouteGuard';
import PeopleExplorerPublicPage from './PeopleExplorerPublicPage';
import AnimalExplorerPublicPage from './AnimalExplorerPublicPage';
// Prepared ahead of release; renders a noindex not-available page until the owner opens its public surface.
import BookExplorerPublicPage from './BookExplorerPublicPage';
import AnimalExplorerSeoPage from './AnimalExplorerSeoPage';
import MusicExplorerPublicPage from './MusicExplorerPublicPage';
import MusicExplorerSeoPage from './MusicExplorerSeoPage';
import WorldExplorerPublicPage from './WorldExplorerPublicPage';
import WorldExplorerSeoPage from './WorldExplorerSeoPage';
import ContractorCompliancePortal from './ContractorCompliancePortal';
import CredentialRenewalHolderPortal from './CredentialRenewalHolderPortal';
import TvPairing from './TvPairing';

const ServiceCommandCentre = React.lazy(() => import('./ServiceCommandCentre'));

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #334155, #881337)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18 }}>
      Loading...
    </div>
  );
  return user ? children : <Navigate to="/login" />;
}

function TvPairingRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#080d18', display: 'grid', placeItems: 'center', color: '#fff', fontSize: 18 }}>Loading…</div>
  );
  if (!user) {
    const returnTo = location.pathname + location.search;
    return <Navigate to={'/login?redirect=' + encodeURIComponent(returnTo)} replace />;
  }
  return <TvPairing />;
}

// Combat PRO (/battletracker) access guard. UX layer only - the authoritative
// enforcement is server-side on the battletracker API blueprint. Requires a valid
// VISINVOKE session AND the server-computed combat_system_pro entitlement (from
// /v1/auth/me - never a client-provided flag). Unauthenticated: redirect to login
// preserving a safe internal return to /battletracker. Authenticated but not
// entitled: fail closed to the established Combat PRO product dashboard. The DCC
// bootstrap path is unchanged: it establishes the same session this guard reads.
function CombatProRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #334155, #881337)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18 }}>
      Loading...
    </div>
  );
  if (!user) return <Navigate to={'/login?redirect=' + encodeURIComponent('/battletracker')} replace />;
  const entitled = !!(user.entitlements && user.entitlements.combat_system_pro);
  if (!entitled) return <Navigate to="/dashboard/battletracker" replace />;
  return children;
}

// Global scroll-to-top: on every route (pathname) change, scroll the window
// to the top. One reusable listener for the entire app - no page-specific
// scroll code needed, and it covers every current and future route. Renders
// nothing. Only reacts to pathname changes, so modals/overlays/dialogs that
// don't change the route are unaffected.
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [pathname]);
  return null;
}

// SupportModal only renders on non-admin routes
function ConditionalSupportModal() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  // Combat PRO (/battletracker) is a self-contained product surface; the portal Support widget is not
  // part of it and must not float over its pages.
  if (location.pathname.startsWith('/battletracker')) return null;
  return <SupportModal />;
}

// Customer auth provider only mounts on non-admin routes
// This prevents /v1/auth/me from firing on admin pages and triggering
// the api.js 401 interceptor which would redirect to /login
function CustomerRoutes() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public */}
        <Route path="/login"             element={<Login />} />
        <Route path="/register"          element={<Register />} />
        <Route path="/verify-email"      element={<VerifyEmail />} />
        <Route path="/reset-password"    element={<ResetPassword />} />
        <Route path="/account-suspended" element={<AccountSuspended />} />
        <Route path="/account-locked"    element={<AccountLocked />} />
        <Route path="/people-explorer"   element={<PeopleExplorerPublicPage />} />
        <Route path="/animal-explorer"   element={<AnimalExplorerPublicPage />} />
        <Route path="/book-explorer"     element={<BookExplorerPublicPage />} />
        <Route path="/animal-explorer/species/:slug" element={<AnimalExplorerSeoPage kind="species" />} />
        <Route path="/animal-explorer/compare/:left/:right" element={<AnimalExplorerSeoPage kind="compare" />} />
        <Route path="/animal-explorer/questions/:slug" element={<AnimalExplorerSeoPage kind="question" />} />
        <Route path="/music-explorer"   element={<MusicExplorerPublicPage />} />
        <Route path="/music-explorer/artist/:slug" element={<MusicExplorerSeoPage kind="artist" />} />
        <Route path="/music-explorer/release-group/:slug" element={<MusicExplorerSeoPage kind="release-group" />} />
        <Route path="/music-explorer/discography/:slug" element={<MusicExplorerSeoPage kind="discography" />} />
        <Route path="/music-explorer/compare/:left/:right" element={<MusicExplorerSeoPage kind="compare" />} />
        <Route path="/world-explorer"   element={<WorldExplorerPublicPage />} />
        <Route path="/world-explorer/country/:slug" element={<WorldExplorerSeoPage kind="country" />} />
        <Route path="/world-explorer/compare/:left/:right" element={<WorldExplorerSeoPage kind="compare" />} />
        <Route path="/world-explorer/questions/:slug" element={<WorldExplorerSeoPage kind="question" />} />
        <Route path="/contractor-compliance/portal" element={<ContractorCompliancePortal />} />
        <Route path="/credential-renewal/holder" element={<CredentialRenewalHolderPortal />} />
        <Route path="/tv" element={<TvPairingRoute />} />
        {process.env.NODE_ENV === 'development' ? <>
          <Route path="/preview/product/:productKey" element={<ProductDetailPage />} />
          <Route path="/preview/utilities" element={<UtilitiesPage />} />
          <Route path="/preview/utilities/ai-rebuild" element={<AiRebuildPage />} />
          <Route path="/preview/gaming" element={<GamingPage />} />
          <Route path="/preview/rpg/veridian" element={<VeridianPurchasePage />} />
          <Route path="/preview/rpg/veridian/more-info" element={<VeridianMoreInfoPage />} />
          <Route path="/preview/rpg/cybertech/more-info" element={<CyberTechMoreInfoPage />} />
          <Route path="/preview/generators/:slug/more-info" element={<StandaloneGeneratorsPage />} />
        </> : null}

        {/* Protected - dashboard */}
        <Route path="/dashboard"                      element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard/visual"               element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard/professional"         element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard/battlemaps"           element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard/battletracker"        element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/dashboard/battletracker/pricing"          element={<PrivateRoute><CombatProPricingPage /></PrivateRoute>} />
        <Route path="/dashboard/battletracker/membership"       element={<PrivateRoute><CombatProMembershipPage /></PrivateRoute>} />
        <Route path="/dashboard/battletracker/purchase-history" element={<PrivateRoute><CombatProPurchaseHistory /></PrivateRoute>} />
        <Route path="/dashboard/battletracker/tools"           element={<PrivateRoute><StandaloneGeneratorsPage /></PrivateRoute>} />
        <Route path="/dashboard/standalone-generators"      element={<PrivateRoute><StandaloneGeneratorsPage /></PrivateRoute>} />
        <Route path="/dashboard/standalone-generators/:slug/more-info" element={<PrivateRoute><StandaloneGeneratorsPage /></PrivateRoute>} />
        <Route path="/dashboard/gaming"               element={<PrivateRoute><GamingPage /></PrivateRoute>} />
        <Route path="/dashboard/virtual-shop"         element={<PrivateRoute><VirtualShopPage /></PrivateRoute>} />
        <Route path="/dashboard/rpg"                  element={<PrivateRoute><RpgPage /></PrivateRoute>} />
        <Route path="/dashboard/rpg/pricing"          element={<PrivateRoute><RpgPricingPage /></PrivateRoute>} />
        <Route path="/dashboard/rpg/veridian"         element={<PrivateRoute><VeridianPurchasePage /></PrivateRoute>} />
        <Route path="/dashboard/rpg/veridian/characters" element={<PrivateRoute><VeridianCharactersPage /></PrivateRoute>} />
        <Route path="/dashboard/rpg/veridian/more-info" element={<PrivateRoute><VeridianMoreInfoPage /></PrivateRoute>} />
        <Route path="/dashboard/rpg/cybertech"        element={<PrivateRoute><CyberTechPurchasePage /></PrivateRoute>} />
        <Route path="/dashboard/rpg/cybertech/more-info" element={<PrivateRoute><CyberTechMoreInfoPage /></PrivateRoute>} />
        <Route path="/dashboard/rpg/cybertech/play" element={<Navigate to="/dashboard/rpg/cybertech" replace />} />
        <Route path="/dashboard/security"             element={<PrivateRoute><SecurityPage /></PrivateRoute>} />
        <Route path="/session-guard/setup" element={<PrivateRoute><SessionGuardSetup /></PrivateRoute>} />
        <Route path="/session-guard/evidence" element={<PrivateRoute><SessionGuardEvidence /></PrivateRoute>} />
        {/* Uniform full-width product detail pages, reached from a category
            card's CLICK HERE FOR MORE INFO pill. One route, one template,
            content from config/productDetails.js. */}
        <Route path="/dashboard/product/:productKey"  element={<PrivateRoute><ProductDetailPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities"            element={<PrivateRoute><UtilitiesPage /></PrivateRoute>} />
        <Route path="/email-ai" element={<PrivateRoute><EmailAIPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities/free"       element={<PrivateRoute><FreeUtilitiesPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities/paid"       element={<PrivateRoute><PaidUtilitiesPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities/service-command-centre" element={<PrivateRoute><React.Suspense fallback={null}><ServiceCommandCentre /></React.Suspense></PrivateRoute>} />
        <Route path="/dashboard/utilities/backup" element={<PrivateRoute><BackupUtilityPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities/pdf-toolkit" element={<PrivateRoute><PdfToolkitPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities/ai-rebuild" element={<PrivateRoute><AiRebuildPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities/registry" element={<PrivateRoute><RegistryUtilitiesPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities/registry/:categoryId" element={<PrivateRoute><RegistryCategoryPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities/tech-hub" element={<PrivateRoute><TechHubPage /></PrivateRoute>} />
        <Route path="/dashboard/utilities/tech-hub/:toolSlug" element={<PrivateRoute><TechHubToolPage /></PrivateRoute>} />
        <Route path="/admin/service-command-centre-master-control" element={<ServiceCommandCentreMasterControl />} />
        <Route path="/dashboard/growth-pricing"       element={<PrivateRoute><GrowthPricing /></PrivateRoute>} />
        <Route path="/dashboard/generator-growth-pricing" element={<PrivateRoute><GeneratorGrowthPricing /></PrivateRoute>} />
        <Route path="/desktop-command-center"         element={<PrivateRoute><DesktopCommandCenter /></PrivateRoute>} />
        <Route path="/dashboard/technician/history"  element={<PrivateRoute><TechnicianCloudHistory /></PrivateRoute>} />
        <Route path="/dashboard/technician/apply"    element={<PrivateRoute><TechnicianApplication /></PrivateRoute>} />

        {/* Protected - other pages */}
        <Route path="/upgrade"    element={<PrivateRoute><Upgrade /></PrivateRoute>} />
        <Route path="/manage"     element={<PrivateRoute><Manage /></PrivateRoute>} />
        <Route path="/generators" element={<PrivateRoute><Generators /></PrivateRoute>} />
        <Route path="/search"     element={<PrivateRoute><SearchResults /></PrivateRoute>} />
        <Route path="/success"    element={<PrivateRoute><PaymentSuccess /></PrivateRoute>} />
        <Route path="/cancel"     element={<PrivateRoute><PaymentCancel /></PrivateRoute>} />
        <Route path="/workspace"  element={<PrivateRoute><Workspace /></PrivateRoute>} />
        <Route path="/quick-edit" element={<PrivateRoute><QuickEditPage /></PrivateRoute>} />
        <Route path="/generating" element={<PrivateRoute><GeneratingResult /></PrivateRoute>} />
        <Route path="/engine"     element={<PrivateRoute><EngineWrapper /></PrivateRoute>} />
        <Route path="/animate"           element={<PrivateRoute><AnimatePage /></PrivateRoute>} />
        <Route path="/animation-credits" element={<PrivateRoute><AnimationCreditsPage /></PrivateRoute>} />
        <Route path="/battlemaps/engines" element={<PrivateRoute><BattlemapsEngines /></PrivateRoute>} />
        <Route path="/battlemaps/pricing" element={<PrivateRoute><BattlemapsPricing /></PrivateRoute>} />
        {/* Declared before /battlemaps/:engine so "packs" is never read as an
            engine name. React Router ranks static segments above dynamic ones,
            but the order is explicit here so it does not depend on that. */}
        <Route path="/battlemaps/packs" element={<PrivateRoute><BattlemapsPacks /></PrivateRoute>} />
        <Route path="/dnd/engines"        element={<PrivateRoute><DnDEngines /></PrivateRoute>} />
        <Route path="/professional/engines" element={<PrivateRoute><ProfessionalEngines /></PrivateRoute>} />
        <Route path="/battlemaps/:engine/library" element={<PrivateRoute><BattlemapGenerators /></PrivateRoute>} />
        <Route path="/battlemaps/:engine"         element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/battletracker" element={<CombatProRoute><BattleTracker /></CombatProRoute>} />
        <Route path="/combat-pro/campaign/:campaignId/messages" element={<PrivateRoute><CombatProPlayerMessagesPage /></PrivateRoute>} />
        <Route path="/rewards"       element={<PrivateRoute><RewardsPage /></PrivateRoute>} />

        {/* Catch-all for customer routes */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </AuthProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <StaffAuthProvider>
        <Routes>
          {/* Preserve the legacy master-control route's existing customer+staff context. */}
          <Route path="/admin/service-command-centre-master-control" element={<CustomerRoutes />} />

          {/* Staff admin routes — fully isolated, no customer AuthProvider */}
          <Route path="/admin/login"     element={<StaffLogin />} />
          <Route path="/admin/mfa-setup" element={<StaffMFASetup />} />
          <Route path="/admin/mfa-verify" element={<StaffMFAVerify />} />
          <Route path="/admin/staff-verify" element={<StaffMFAElevate />} />
          <Route path="/admin/dashboard" element={<StaffAdminRouteGuard><StaffDashboard /></StaffAdminRouteGuard>} />
          <Route path="/admin/staff"     element={<StaffAdminRouteGuard><StaffDashboard /></StaffAdminRouteGuard>} />
          <Route path="/admin/security"  element={<StaffAdminRouteGuard><StaffDashboard /></StaffAdminRouteGuard>} />
          <Route path="/admin/security-operations" element={<StaffAdminRouteGuard><SecurityAdminOps /></StaffAdminRouteGuard>} />
          <Route path="/admin/audit"     element={<StaffAdminRouteGuard><StaffDashboard /></StaffAdminRouteGuard>} />
          <Route path="/admin/sessions"  element={<StaffAdminRouteGuard><StaffDashboard /></StaffAdminRouteGuard>} />
          <Route path="/admin/dcc_overview" element={<StaffAdminRouteGuard><StaffDashboard /></StaffAdminRouteGuard>} />
          <Route path="/admin/dcc_news"  element={<StaffAdminRouteGuard><StaffDashboard /></StaffAdminRouteGuard>} />
          <Route path="/admin/invite/:token"   element={<StaffInviteAccept />} />
          <Route path="/admin/virtual-shop" element={<StaffAdminRouteGuard><StaffDashboard /></StaffAdminRouteGuard>} />
          <Route path="/admin/reset-password"    element={<StaffResetPassword />} />
          <Route path="/admin/mfa-recovery"      element={<StaffMFARecovery />} />
          <Route path="/admin"           element={<Navigate to="/admin/login" />} />
          {/* Never allow an Admin namespace miss to mount CustomerRoutes or its /login fallback. */}
          <Route path="/admin/*" element={<StaffAdminNamespaceFallback />} />

          {/* All customer routes handled by CustomerRoutes which mounts AuthProvider */}
          <Route path="/*" element={<CustomerRoutes />} />
        </Routes>
        <ScrollToTop />
        <ConditionalSupportModal />
        <GoToTop />
      </StaffAuthProvider>
    </BrowserRouter>
  );
}

export default App;
